// projection.ts — 다년 손익 추정 순수 계산: 부품×연도 매출/손익/손익율 매트릭스.
//
// 백엔드 `apps/api/api/core/cost/projection.py` 와 동일한 수식/결과를 유지해야 한다
// (CLAUDE.md 패리티 — 한쪽 수정 시 양쪽 + 테스트 동시 수정).
//
// Excel 출처: `내부원가보고` 시트(정본). #9 의 CR-시트 framing 과 다르다.
//
// 핵심 framing (`내부원가보고` 정본):
//   - 내부원가(SOP) 는 모든 연도에 걸쳐 고정(SOP 값 그대로) — CR 로 재빌드하지 않는다.
//   - 견적가는 CR 시리즈를 따른다: SOP..Y+3 인하 후 동결(Y+4..Y+9 = Y+3 값).
//   - 손익 = (견적가 − SOP_내부원가) × 부품수량 → 손익율은 연차마다 하락(9.1% → ~2.5%).
//
// 수식:
//   부품수량(연도) = 연간대수 × 차종당 부품수            // annualVolume 로 직접 입력
//   견적가(연도)   = CR 견적가 (SOP..Y+3, then 동결)      // quotePrices (이미 CR+동결)
//   매출(연도)     = 견적가(연도) × 부품수량(연도)
//   손익액(연도)   = (견적가(연도) − SOP_내부원가) × 부품수량(연도)
//   손익율(연도)   = 손익액 / 매출 = (견적가 − SOP_내부원가) / 견적가
//   총 매출/손익   = Σ over years (per part) and Σ over parts (grand total)
//   총 손익율      = 총손익액 / 총매출
//
// 상위 모듈은 import only (재정의 금지): applyCR (연도별 견적가).
// 견적가 동결: CR 모듈은 SOP..Y+3(4개)만 준다 — Y+4..Y+9 는 Y+3 값을 반복한다.

import { applyCR, type CRMode } from "@/lib/cost/cr";

// CR 인하 연차 수(SOP 제외): SOP/Y+1/Y+2/Y+3 → 4개 견적가 후 동결.
export const CR_FREEZE_YEAR_INDEX = 3;

export interface PartPlan {
  name: string; // 부품명 (CNC / BOLT / NUT ...)
  sopInternalCost: number; // SOP 내부원가 (전 연도 고정)
  quotePrices: number[]; // 견적가(연도) — 이미 CR+동결, length == years.length
  annualVolume: number[]; // 부품수량(연도) = 연간대수 × 차종당 부품수, length == years.length
}

export interface YearPnL {
  year: number; // 연도 (예: 2028)
  quotePrice: number; // 견적가(연도)
  volume: number; // 부품수량(연도)
  revenue: number; // 매출 = 견적가 × 수량
  profit: number; // 손익액 = (견적가 − SOP_내부원가) × 수량
  profitRate: number; // 손익율 = 손익액 / 매출
}

export interface PartProjection {
  name: string;
  sopInternalCost: number;
  years: YearPnL[];
  totalRevenue: number; // Σ 매출 over years
  totalProfit: number; // Σ 손익 over years
  totalProfitRate: number; // 총손익 / 총매출
}

export interface ProjectionResult {
  years: number[];
  parts: PartProjection[];
  totalRevenue: number; // Σ over parts
  totalProfit: number; // Σ over parts
  totalProfitRate: number; // 총손익 / 총매출
}

function profitRate(revenue: number, profit: number): number {
  return revenue > 0 ? profit / revenue : 0;
}

export interface FrozenQuoteInput {
  materialCost: number; // 재료비계
  processingCost: number; // 가공비
  sopInternalCost: number; // SOP 내부원가
  sopSupplyPrice: number; // SOP 견적가
  nYears: number; // 연도 수 (동결 길이)
  inhouse?: boolean; // 사급구분 — 내작(true, 기본)
  mode?: CRMode; // 'precise'(기본) | 'simple'
}

// CR 견적가 시리즈(SOP..Y+3)를 만들고 nYears 길이까지 동결(Y+3 값 반복)한다.
export function frozenQuotePrices({
  materialCost,
  processingCost,
  sopInternalCost,
  sopSupplyPrice,
  nYears,
  inhouse = true,
  mode = "precise",
}: FrozenQuoteInput): number[] {
  if (nYears <= 0) throw new Error("nYears must be >= 1");
  const rows = applyCR({
    materialCost,
    processingCost,
    sopInternalCost,
    sopSupplyPrice,
    years: CR_FREEZE_YEAR_INDEX,
    inhouse,
    mode,
  });
  const prices = rows.map((r) => r.supplyPrice); // length == CR_FREEZE_YEAR_INDEX + 1 (4)
  if (nYears <= prices.length) return prices.slice(0, nYears);
  const frozen = prices[prices.length - 1];
  return [...prices, ...Array<number>(nYears - prices.length).fill(frozen)];
}

// 부품×연도 다년 손익을 산출한다 (내부원가보고 framing — SOP 내부원가 고정).
export function multiYearPnL(
  parts: PartPlan[],
  { years }: { years: number[] },
): ProjectionResult {
  if (years.length === 0) throw new Error("years must be non-empty");
  const n = years.length;

  const partProjections: PartProjection[] = [];
  let grandRevenue = 0;
  let grandProfit = 0;

  for (const plan of parts) {
    if (plan.quotePrices.length !== n) {
      throw new Error(
        `${plan.name}: quotePrices length ${plan.quotePrices.length} != years ${n}`,
      );
    }
    if (plan.annualVolume.length !== n) {
      throw new Error(
        `${plan.name}: annualVolume length ${plan.annualVolume.length} != years ${n}`,
      );
    }

    const cells: YearPnL[] = [];
    let partRevenue = 0;
    let partProfit = 0;
    for (let i = 0; i < n; i++) {
      const quote = plan.quotePrices[i];
      const volume = plan.annualVolume[i];
      const revenue = quote * volume;
      const profit = (quote - plan.sopInternalCost) * volume;
      cells.push({
        year: years[i],
        quotePrice: quote,
        volume,
        revenue,
        profit,
        profitRate: profitRate(revenue, profit),
      });
      partRevenue += revenue;
      partProfit += profit;
    }

    partProjections.push({
      name: plan.name,
      sopInternalCost: plan.sopInternalCost,
      years: cells,
      totalRevenue: partRevenue,
      totalProfit: partProfit,
      totalProfitRate: profitRate(partRevenue, partProfit),
    });
    grandRevenue += partRevenue;
    grandProfit += partProfit;
  }

  return {
    years: [...years],
    parts: partProjections,
    totalRevenue: grandRevenue,
    totalProfit: grandProfit,
    totalProfitRate: profitRate(grandRevenue, grandProfit),
  };
}
